import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { capitalizeFirstLetter, extractItemsFromStore, getCurrentLanguage } from '../../helpers'
import PropTypes from 'prop-types'
import { CalendarField, CustomField } from '../CustomField'
import Alert from '../Alert'
import Loader from '../Loader'
import SearchFilter from '../SearchFilter'
import Button from '../Button'
import DateRangePicker from '../DateRangePicker'
import DestinationsList from '../DestinationsList'
import DepartureList from '../DepartureList'
import { fetchPossibleParams } from '../../api/fetchPossibleParams'
import {
    setPossibleDestinations,
    setPossibleDepartures,
    setPossibleTransports,
    setPopularDestinations
} from '../../actions/dataActions'
import {
    resetSelection,
    setAdults,
    setTransport,
    resetParticipants,
    setFormField
} from '../../actions/formActions'
import destinationIcon from '@assets/img/search-engine/destination-icon.svg';
import departureIcon from '@assets/img/search-engine/departure-icon.svg';
import calendarIcon from '@assets/img/search-engine/calendar-icon.svg';
import participantIcon from '@assets/img/search-engine/participants-icon.svg';
import "./style.scss"
import Participants from '../Participants'
import { DEFAULT_ADULT_COUNT, DEFAULT_CHILDREN_COUNT } from '../../../search-engine/constants'
import { PAGE_SEARCH, PAGE_SEARCH_CHARTERS } from '../../consts/endpoints'
import SearchChartersFilter from '../SearchFilter/SearchChartersFilter'
import { TRANSPORT_CHARTER_TYPE } from '../../consts/data'

const TripSearchForm = ({
    customDestination,
    customTransport,
    form,
    data,
    setPopularDestinations,
    setPossibleDestinations,
    setPossibleDepartures,
    setPossibleTransports,
    setTransport,
    setAdults,
    resetSelection,
    resetParticipants,
    setFormField
}) => {
    const { t, i18n } = useTranslation()
    const [isLoading, setIsLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState(null)
    const [destinationPlaceholder, setDestinationPlaceholder] = useState(null)
    const [departurePlaceholder, setDeparturePlaceholder] = useState(null)
    const [participantsPlaceholder, setParticipantsPlaceholder] = useState(null)
    const [destinationResetKey, setDestinationResetKey] = useState(0)
    const [isValidParticipants, setIsValidParticipants] = useState(true)
    const [urlParamsApplied, setUrlParamsApplied] = useState(false)
    const honeypotRef = useRef(null)

    useEffect(() => {
        const currentLang = getCurrentLanguage()

        i18n.changeLanguage(currentLang).then(r => null)
    }, [i18n])

    useEffect(() => {
        if (!isLoading && !urlParamsApplied) {
            const params = new URLSearchParams(window.location.search);
            const entries = Array.from(params.entries());

            entries.forEach(([key, value]) => {
                if (key in form) {
                    setFormField(key, value);
                }
            })

            setFormField('cities', params.getAll('city[]'));

            setUrlParamsApplied(true);
        }
    }, [isLoading, urlParamsApplied])

    useEffect(() => {
        const initialTransport = customTransport || 'samolot'

        fetchPossibleParams(customTransport)
            .then((data) => {
                setPopularDestinations(data.popular)
                setPossibleDestinations(data.countryRegion)
                setPossibleDepartures(data.departureFrom)
                setPossibleTransports(data.transports)

                setAdults(data.defaultValues.adults)
                setTransport(initialTransport)
            })
            .catch((err) => setErrorMessage(err.message))
            .finally(() => {
                setIsLoading(false)
            });
    }, [])

    useEffect(() => {
        if (customDestination) {
            const destinationValue = capitalizeFirstLetter(customDestination);
            
            // Sprawdź czy jest parametr typ[]=objazdowa w URL
            const params = new URLSearchParams(window.location.search);
            const typValues = params.getAll('typ[]');
            const hasObjazdowa = typValues.includes('objazdowa');
            
            if (hasObjazdowa) {
                // Jeśli jest objazdowa, zamień kraj na "Kraj.Wyc. Objazdowe"
                const objazdowaValue = `${destinationValue}.Wyc. Objazdowe`;
                setFormField('kierunek', objazdowaValue);
            } else {
                setFormField('kierunek', destinationValue);
            }
        }
    }, [])

    useEffect(() => {
        const defaultPlaceholder = t('form.destinations.field_placeholder');
        const hasObjazdowa = form.kierunek.toLowerCase().includes('objazdowa');
        const cityValues = Array.isArray(form.cities) ? form.cities : [];
        const hasCity = cityValues.length > 0;
        
        let destinationsLabel = defaultPlaceholder;
        
        if (form.kierunek && form.kierunek !== '') {
            if (hasObjazdowa) {
                // Jeśli jest typ[]=objazdowa, wyświetl tylko kraj (pierwsza część)
                const kierunekParts = form.kierunek.split(';');
                const firstPart = kierunekParts[0];
                const parts = firstPart.split('.');
                destinationsLabel = parts[0]; // Tylko kraj
            } else if (hasCity) {
                // Jeśli jest city[], wyświetl tylko miasto
                destinationsLabel = cityValues.join(', ');
            } else {
                // Standardowa logika - użyj extractItemsFromStore
                destinationsLabel = extractItemsFromStore(form.kierunek, defaultPlaceholder);
            }
        }

        let label = destinationsLabel;

        if (form.hotel && form.hotel !== '') {
            label = destinationsLabel === defaultPlaceholder ? form.hotel : `${destinationsLabel}, ${form.hotel}`;
        }

        setDestinationPlaceholder(label);
    }, [form.kierunek, form.hotel, form.cities])

    useEffect(() => {
        const departureLabel = extractItemsFromStore(form.z, t('form.departures.field_placeholder'));
        setDeparturePlaceholder(departureLabel);
    }, [form.z])

    useEffect(() => {
        const defaultPlaceholder = t('form.participants.default_placeholder');
        const { dorosli, dzieci } = form;
        let placeholder = null;

        if (Number(dorosli) === DEFAULT_ADULT_COUNT && dzieci.length === DEFAULT_CHILDREN_COUNT.length) {
            placeholder = defaultPlaceholder
        } else {
            placeholder = dzieci.length > 0 ?
                `${dorosli} + ${dzieci.length}` :
                `${dorosli} ${t('form.participants.adult')}`;
        }

        setParticipantsPlaceholder(placeholder)
    }, [form.dorosli, form.dzieci])

    if (isLoading) {
        return <Loader />
    }

    if (errorMessage !== null) {
        return <Alert variant="danger" header={t('common.error_header')} message={errorMessage} />
    }

    const submitHandler = (e) => {
        e.preventDefault();

        if (honeypotRef.current && honeypotRef.current.value.trim() !== '') {
            return false;
        }

        if (!isValidParticipants) {
            return false;
        }

        const params = new URLSearchParams(window.location.search);
        let { kierunek, cities: selectedCities, ...filterForm } = form;
        
        // Sprawdź czy wybrano "Wyc. Objazdowe" w jakimś kraju
        let hasObjazdowa = false;
        let countryName = null;
        const derivedCities = [];
        
        if (kierunek) {
            const kierunekValues = kierunek.split(';');
            const processedKierunekValues = [];
            
            kierunekValues.forEach(val => {
                const lowerVal = val.toLowerCase();
                const parts = val.split('.');
                
                // Najpierw sprawdź czy to "Wyc. Objazdowe" - przed sprawdzaniem długości parts
                if (lowerVal.includes('wyc. objazdowe') || lowerVal.includes('objazdowa')) {
                    hasObjazdowa = true;
                    
                    // Dla "Wyc. Objazdowe" dodaj tylko kraj (pierwsza część)
                    if (parts.length > 0) {
                        countryName = parts[0];
                        
                        if (!processedKierunekValues.includes(countryName)) {
                            processedKierunekValues.push(countryName);
                        }
                    }
                } else if (parts.length === 3) {
                    // Standardowy przypadek z 3 poziomami (kraj.region.miasto)
                    derivedCities.push(parts[2]);
                    const regionKey = parts[0] + '.' + parts[1];
                    if (!processedKierunekValues.includes(regionKey)) {
                        processedKierunekValues.push(regionKey);
                    }
                } else {
                    // Standardowy przypadek - dodaj wartość jak jest
                    processedKierunekValues.push(val);
                }
            });
            
            kierunek = processedKierunekValues.join(';');
        }

        if (kierunek) {
            const citiesToSubmit = [...new Set([
                ...(Array.isArray(selectedCities) ? selectedCities : []),
                ...derivedCities
            ])];

            citiesToSubmit.forEach(city => params.append('city[]', city));
        }

        if (hasObjazdowa) {
            params.delete('typ[]');
            params.set('min-dni', '1');
            params.set('max-dni', '21');
            params.append('typ[]', 'objazdowa');
        }

        Object.entries(filterForm)
            .filter(([, value]) => Array.isArray(value) ? value.length > 0 : value)
            .forEach(([key, value]) => {
                if (key === 'tripDirection' && customTransport !== TRANSPORT_CHARTER_TYPE) {
                    return;
                }

                const queryKey = key === 'lastMinute' ? 'last-minute' : key;
                params.set(queryKey, value);
            });


        params.delete('page');
        params.delete('strona');

        const redirectTarget = customTransport == 'czarter' ? PAGE_SEARCH_CHARTERS : PAGE_SEARCH;
        const query = params.toString();

        if (kierunek) {
            window.location.href = `${redirectTarget}/${kierunek}${query ? `?${query}` : ''}`
        } else {
            window.location.href = `${redirectTarget}${query ? `?${query}` : ''}`
        }

        return false;
    }

    const resetDepartures = () => {
        resetSelection('z')
    }

    const resetDestinations = () => {
        resetSelection('kierunek')
        setFormField('cities', [])
        setFormField('hotel', '')
        setDestinationResetKey(prev => prev + 1)
    }

    const resetParticipantsHandler = () => {
        resetParticipants()
    }

    return (
        <form onSubmit={submitHandler} className="trip-form">
            <div>
                <input
                    ref={honeypotRef}
                    type="hidden"
                    name="required"
                    defaultValue=""
                    autoComplete="off"
                    tabIndex="-1"
                    aria-hidden="true"
                    className="trip-required"
                />
                <CustomField
                    icon={destinationIcon}
                    title={t('form.destinations.field_label')}
                    placeholder={destinationPlaceholder}
                    modalId="destinations"
                    extraCssClass="trip-field--destinations box-shadow"
                    onReset={resetDestinations}
                >
                    <DestinationsList
                        popular={data.popularDestinations}
                        destinations={data.possibleDestinations}
                        resetTrigger={destinationResetKey}
                    />
                </CustomField>

                <CustomField
                    icon={departureIcon}
                    title={t('form.departures.field_label')}
                    placeholder={departurePlaceholder}
                    modalId="departures"
                    extraCssClass="trip-field--departures box-shadow"
                    onReset={resetDepartures}
                >
                    <DepartureList
                        departures={data.possibleDepartures}
                    />
                </CustomField>

                <CalendarField
                    icon={calendarIcon}
                    extraCssClass="trip-field--calendar box-shadow"
                >
                    <DateRangePicker
                        startLabel={t(`form.dates.start_field_label`)}
                        endLabel={t(`form.dates.end_field_label`)}
                        endPlaceholder={t(`form.dates.end_field_placeholder`)}
                    />
                </CalendarField>

                <CustomField
                    icon={participantIcon}
                    title={t('form.participants.field_label')}
                    placeholder={participantsPlaceholder}
                    modalId="participants"
                    extraCssClass="trip-field--participants box-shadow"
                    onReset={resetParticipantsHandler}
                    isValid={isValidParticipants}
                >
                    <Participants onValidityChange={setIsValidParticipants}/>
                </CustomField>

                <Button
                    label={t('common.btn_search')}
                    variant="primary"
                    type="submit"
                    extraCss="trip-btn--search box-shadow animation-bg-color"
                />
            </div>
            {customTransport === TRANSPORT_CHARTER_TYPE ? (
                <SearchChartersFilter />
            ) : (
                <SearchFilter />
            )}
        </form>
    )
};

TripSearchForm.propTypes = {
    customDestination: PropTypes.string,
    form: PropTypes.object,
    data: PropTypes.object,
    setPopularDestinations: PropTypes.func,
    setPossibleDestinations: PropTypes.func,
    setPossibleDepartures: PropTypes.func,
    setPossibleTransports: PropTypes.func,
    setAdults: PropTypes.func,
    setTransport: PropTypes.func,
    resetSelection: PropTypes.func,
    setFormField: PropTypes.func,
};

const mapStateToProps = (state) => ({
    form: state.formReducer,
    data: state.dataReducer
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setPopularDestinations,
            setPossibleDestinations,
            setPossibleDepartures,
            setPossibleTransports,
            setAdults,
            setTransport,
            resetSelection,
            resetParticipants,
            setFormField
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(TripSearchForm)
